import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { UsersThree, ShieldStar, ChartBar, EnvelopeOpen, ClockClockwise, ArrowUUpLeft, ChartLine, Plus, BookOpen, Pulse, Clock, SignIn, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { CoePeopleTab } from "@/components/admin/coe/PeopleTab";
import { CoeRolesTab } from "@/components/admin/coe/RolesTab";
import { CoeWorkloadTab } from "@/components/admin/coe/WorkloadTab";
import { CoeInvitesTab } from "@/components/admin/coe/InvitesTab";
import { CoeAuditTab } from "@/components/admin/coe/AuditTab";
import { CoeReactivationTab } from "@/components/admin/coe/ReactivationTab";
import AdminPermissions from "@/pages/admin/Permissions";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";
import { StatTile } from "@/pages/admin/Finance";
import api from "@/lib/api";

/* ============================================================================
   AdminCoe — Command Center for managing the COE team, roles & workload.

   iter101.34 — Canonical 5-tile KPI strip at page level (Online now · Total
   users · Logins today · Avg session · Disabled), replacing the inline
   TeamHeartbeat pulse strip. Each tile is click-through and pushes a
   pre-set filter into the Users tab. Data pulled from /admin/coe/heartbeat
   (already exists) + counted client-side from /admin/coe/people.
   ========================================================================== */

const TABS = [
    { id: "people",              label: "Users",                Icon: UsersThree },
    { id: "roles",               label: "Roles",                Icon: ShieldStar },
    { id: "permissions-matrix",  label: "Permissions matrix",   Icon: BookOpen },
    { id: "workload",            label: "Workload",             Icon: ChartBar },
    { id: "invites",             label: "Invites",              Icon: EnvelopeOpen },
    { id: "reactivation",        label: "Reactivation Queue",   Icon: ArrowUUpLeft },
    { id: "audit",               label: "Audit",                Icon: ClockClockwise },
];

const KPI_POLL_MS = 45_000;

const fmtMins = (m) => {
    if (m == null) return "—";
    if (m < 60) return `${Math.round(m)}m`;
    const h = Math.floor(m / 60);
    const r = Math.round(m - h * 60);
    return r > 0 ? `${h}h ${r}m` : `${h}h`;
};

export default function AdminCoe() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "people";
    const [tab, setTab] = useState(initialTab);
    /* iter100.227 — Optional `?filter=<staff_type>` pre-selects the People-tab
       filter card (e.g. the "Add Technician" flow deep-links here). */
    const initialFilter = searchParams.get("filter") || null;
    const [counts, setCounts] = useState({ people: 0, roles: 0, invites: 0 });
    /* The Add-User dialog lives inside PeopleTab; the header button toggles
       it via this signal counter so we don't have to lift the form state. */
    const [addUserSignal, setAddUserSignal] = useState(0);

    /* KPI-strip data: heartbeat is polled every 45s so the "Online now"
       stat feels live; people list is refetched every time the tab or
       add-user signal fires (cheap enough at 500-user cap). */
    const [heartbeat, setHeartbeat] = useState(null);
    const [staffCount, setStaffCount] = useState({ total: 0, disabled: 0 });

    useEffect(() => {
        if (tab === searchParams.get("tab")) return;
        if (tab === "people") {
            searchParams.delete("tab");
            setSearchParams(searchParams, { replace: true });
        } else {
            searchParams.set("tab", tab);
            setSearchParams(searchParams, { replace: true });
        }
    }, [tab, searchParams, setSearchParams]);

    useEffect(() => {
        Promise.all([
            api.get("/admin/coe/people"),
            api.get("/admin/coe/roles"),
            api.get("/admin/coe/invites"),
        ]).then(([p, r, i]) => {
            const people = p.data || [];
            setCounts({
                people: people.length,
                roles: (r.data || []).length,
                invites: (i.data || []).length,
            });
            setStaffCount({
                total: people.length,
                disabled: people.filter((x) => x.is_active === false).length,
            });
        }).catch(() => {});
    }, [tab, addUserSignal]);

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const r = await api.get("/admin/coe/heartbeat");
                if (!cancelled) setHeartbeat(r.data);
            } catch { /* silent — decorative */ }
        };
        tick();
        const int = setInterval(tick, KPI_POLL_MS);
        return () => { cancelled = true; clearInterval(int); };
    }, []);

    const triggerAdd = () => {
        if (tab !== "people") setTab("people");
        setAddUserSignal((n) => n + 1);
    };

    /* iter101.34 — Deep-linking from KPI tile → Users tab with the matching
       chip pre-selected. We push a `status` query-param that PeopleTab reads
       and syncs to its FilterChipRow. */
    const goToPeopleWithStatus = (status) => {
        if (tab !== "people") setTab("people");
        const next = new URLSearchParams(searchParams);
        next.delete("tab"); // people is default
        if (status && status !== "all") next.set("status", status);
        else next.delete("status");
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="space-y-6" data-testid="admin-coe-page">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Admin · Team &amp; Access</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">COE Command Center</h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                        Roster, roles, workload, invites, and audit — the whole ops team in one workspace.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Link to="/admin/coe/reports">
                        <Button variant="outline" size="sm" className="rounded-full" data-testid="coe-hub-reports-link">
                            <ChartLine size={14} weight="bold" className="mr-1.5" /> Reports
                        </Button>
                    </Link>
                    <Button size="sm" className="rounded-full" onClick={triggerAdd} data-testid="coe-hub-add-user">
                        <Plus size={14} weight="bold" className="mr-1.5" /> Add User
                    </Button>
                </div>
            </div>

            {/* iter101.34 — Canonical 5-tile KPI strip. Every tile is a real
                affordance that lands on the Users tab with the matching
                filter pre-applied. */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="coe-kpi-strip">
                <KpiTileButton onClick={() => goToPeopleWithStatus("all")} testid="coe-kpi-online">
                    <StatTile
                        label="Online now"
                        value={heartbeat?.online_now ?? "—"}
                        Icon={Pulse}
                        tone="emerald"
                    />
                </KpiTileButton>
                <KpiTileButton onClick={() => goToPeopleWithStatus("all")} testid="coe-kpi-total">
                    <StatTile
                        label="Total users"
                        value={staffCount.total || "—"}
                        Icon={UsersThree}
                        tone="primary"
                    />
                </KpiTileButton>
                <KpiTileButton onClick={() => goToPeopleWithStatus("all")} testid="coe-kpi-logins">
                    <StatTile
                        label="Logins today"
                        value={heartbeat?.logins_today ?? "—"}
                        Icon={SignIn}
                        tone="violet"
                    />
                </KpiTileButton>
                <KpiTileButton onClick={() => goToPeopleWithStatus("all")} testid="coe-kpi-avg-session">
                    <StatTile
                        label="Avg session"
                        value={fmtMins(heartbeat?.avg_session_minutes)}
                        Icon={Clock}
                        tone="amber"
                    />
                </KpiTileButton>
                <KpiTileButton onClick={() => goToPeopleWithStatus("disabled")} testid="coe-kpi-disabled">
                    <StatTile
                        label="Disabled"
                        value={staffCount.disabled}
                        Icon={XCircle}
                        tone="rose"
                        urgent={staffCount.disabled > 0}
                    />
                </KpiTileButton>
            </div>

            <AdminSegmentedTabs
                tabs={TABS.map((t) => ({
                    id: t.id,
                    label: t.label,
                    Icon: t.Icon,
                    count:
                        t.id === "people"  ? counts.people  :
                        t.id === "roles"   ? counts.roles   :
                        t.id === "invites" ? counts.invites : null,
                }))}
                active={tab}
                onChange={setTab}
                testid="coe-tabs"
                tabTestidBase="coe-tab"
            />

            <div>
                {tab === "people"              && <CoePeopleTab addSignal={addUserSignal} initialFilter={initialFilter} />}
                {tab === "roles"               && <CoeRolesTab />}
                {tab === "permissions-matrix"  && <AdminPermissions />}
                {tab === "workload"            && <CoeWorkloadTab />}
                {tab === "invites"             && <CoeInvitesTab />}
                {tab === "reactivation"        && <CoeReactivationTab />}
                {tab === "audit"               && <CoeAuditTab />}
            </div>
        </div>
    );
}

function KpiTileButton({ children, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className="text-left rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 hover:-translate-y-0.5 hover:shadow-sm transition-all"
        >
            {children}
        </button>
    );
}
